import { inject, Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';

import {
    CsvAsStringArrays,
    CsvInsightChart,
    CsvRestaurantInsightsChart,
    getPlatformKeysWithFollowers,
    PlatformKey,
} from '@malou-io/package-utils';

import { BoostersGiftsCsvInsightService } from '#modules/statistics-boosters/services/csv/boosters-gifts.service';
import { BoostersReviewsCountCsvInsightV2Service } from '#modules/statistics-boosters/services/csv/boosters-reviews-count-v2.service';
import { BoostersScanCountCsvInsightV2Service } from '#modules/statistics-boosters/services/csv/boosters-scan-count-v2.service';
import { FollowersCsvInsightsV2Service } from '#modules/statistics-e-reputation/services/csv/followers-v2.service';
import { PlatformsRatingsCsvInsightsService } from '#modules/statistics-e-reputation/services/csv/platforms-ratings.service';
import { ReviewsRatingsEvolutionCsvInsightsService } from '#modules/statistics-e-reputation/services/csv/reviews-ratings-evolution.service';
import { ReviewsRatingsTotalCsvInsightsService } from '#modules/statistics-e-reputation/services/csv/reviews-ratings-total.service';
import { SemanticAnalysisDetailsCsvInsightsService } from '#modules/statistics-e-reputation/services/csv/semantic-analysis-details-csv-insights.service';
import { SemanticAnalysisTopicsCsvInsightsService } from '#modules/statistics-e-reputation/services/csv/semantic-analysis-topics-csv-insights.service';
import { GmbVisibilityCsvInsightsService } from '#modules/statistics-seo/services/csv/gmb-visibility.service';
import { KeywordsCsvInsightsServiceV2 } from '#modules/statistics-seo/services/csv/keywords-csv-insights-v2.service';
import { TopKeywordSearchImpressionsService } from '#modules/statistics-seo/services/csv/top-keyword-serach-impressions.service';
import { PublicationsCsvInsightsV3Service } from '#modules/statistics-social-networks/services/csv/publications-v3.service';
import { StoryCsvInsightsService } from '#modules/statistics-social-networks/services/csv/stories.service';
import { AbstractDownloadCsvInsights } from '#modules/statistics/components/download-insights-modal/services/download-csv-insights.abstract.service';
import { CsvStringCreator } from '#shared/helpers/create-csv-string';

interface Params {
    csvInsightChart: CsvRestaurantInsightsChart;
}

@Injectable()
export class DownloadCsvInsightsService extends AbstractDownloadCsvInsights<Params> {
    // Utils
    private readonly _csvStringCreator = inject(CsvStringCreator);

    // SEO
    private readonly _gmbVisibilityCsvInsightsService = inject(GmbVisibilityCsvInsightsService);
    private readonly _topKeywordSearchImpressionsService = inject(TopKeywordSearchImpressionsService);
    private readonly _keywordsCsvInsightsServiceV2 = inject(KeywordsCsvInsightsServiceV2);

    // SOCIAL NETWORKS
    private readonly _storiesCsvInsightsService = inject(StoryCsvInsightsService);
    private readonly _publicationsCsvInsightsV3Service = inject(PublicationsCsvInsightsV3Service);

    // E-REPUTATION
    private readonly _followersCsvInsightsV2Service = inject(FollowersCsvInsightsV2Service);
    private readonly _platformsRatingsCsvInsightsService = inject(PlatformsRatingsCsvInsightsService);
    private readonly _semanticAnalysisTopicsCsvInsightsService = inject(SemanticAnalysisTopicsCsvInsightsService);
    private readonly _semanticAnalysisDetailsCsvInsightsService = inject(SemanticAnalysisDetailsCsvInsightsService);
    private readonly _reviewsRatingsEvolutionCsvInsightsService = inject(ReviewsRatingsEvolutionCsvInsightsService);
    private readonly _reviewsRatingsTotalCsvInsightsService = inject(ReviewsRatingsTotalCsvInsightsService);

    // BOOSTERS
    private readonly _boostersGiftsInsightsService = inject(BoostersGiftsCsvInsightService);
    private readonly _boostersScanCountInsightsV2Service = inject(BoostersScanCountCsvInsightV2Service);
    private readonly _boostersReviewsCountInsightsV2Service = inject(BoostersReviewsCountCsvInsightV2Service);

    getCsvInsightsData$(params: Params): Observable<string | null> {
        const { csvInsightChart } = params;
        let csvData$: Observable<CsvAsStringArrays | null>;
        switch (csvInsightChart) {
            case CsvInsightChart.KEYWORDS:
                csvData$ = this._keywordsCsvInsightsServiceV2.getCsvData$();
                break;
            case CsvInsightChart.GMB_VISIBILITY:
                csvData$ = this._gmbVisibilityCsvInsightsService.getCsvData$();
                break;
            case CsvInsightChart.PUBLICATIONS:
                csvData$ = this._publicationsCsvInsightsV3Service.getCsvData$();
                break;
            case CsvInsightChart.STORIES:
                csvData$ = this._storiesCsvInsightsService.getCsvData$();
                break;
            case CsvInsightChart.ALL_FOLLOWERS:
                csvData$ = this._followersCsvInsightsV2Service.getCsvData$({
                    platformKeys: getPlatformKeysWithFollowers(),
                });
                break;
            case CsvInsightChart.FB_FOLLOWERS:
                csvData$ = this._followersCsvInsightsV2Service.getCsvData$({ platformKeys: [PlatformKey.FACEBOOK] });
                break;
            case CsvInsightChart.IG_FOLLOWERS:
                csvData$ = this._followersCsvInsightsV2Service.getCsvData$({ platformKeys: [PlatformKey.INSTAGRAM] });
                break;
            case CsvInsightChart.TIKTOK_FOLLOWERS:
                csvData$ = this._followersCsvInsightsV2Service.getCsvData$({ platformKeys: [PlatformKey.TIKTOK] });
                break;
            case CsvInsightChart.PLATFORMS_RATINGS:
                csvData$ = this._platformsRatingsCsvInsightsService.getCsvData$();
                break;
            case CsvInsightChart.SEMANTIC_ANALYSIS_TOPICS:
                csvData$ = this._semanticAnalysisTopicsCsvInsightsService.getCsvData$();
                break;
            case CsvInsightChart.SEMANTIC_ANALYSIS_DETAILS:
                csvData$ = this._semanticAnalysisDetailsCsvInsightsService.getCsvData$();
                break;
            case CsvInsightChart.BOOSTERS_SCAN_COUNT:
                csvData$ = this._boostersScanCountInsightsV2Service.getCsvData$();
                break;
            case CsvInsightChart.BOOSTERS_REVIEWS_COUNT:
                csvData$ = this._boostersReviewsCountInsightsV2Service.getCsvData$();
                break;
            case CsvInsightChart.BOOSTERS_WHEEL_OF_FORTUNE_GIFTS_DISTRIBUTION:
                csvData$ = this._boostersGiftsInsightsService.getCsvData$();
                break;
            case CsvInsightChart.REVIEWS_RATINGS_EVOLUTION:
                csvData$ = this._reviewsRatingsEvolutionCsvInsightsService.getCsvData$();
                break;
            case CsvInsightChart.REVIEWS_RATINGS_TOTAL:
                csvData$ = this._reviewsRatingsTotalCsvInsightsService.getCsvData$();
                break;
            case CsvInsightChart.KEYWORD_SEARCH_IMPRESSIONS:
                csvData$ = this._topKeywordSearchImpressionsService.getCsvData$();
                break;
            default:
                return of(null);
        }

        return csvData$.pipe(
            map((csvData) => {
                if (!csvData) {
                    return null;
                }
                return this._csvStringCreator.create(csvData);
            })
        );
    }
}
